import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Autocomplete, Grid, Typography } from "@mui/material";
import FormGridRow from "../../../../common/components/functional/formGridRow/FormGridRow";
import CommonTextField from "../../../../common/components/functional/commonTextField/CommonTextField";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { fetchEezyCompanies } from "../../../invoices/invoiceSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import { selectActiveExpertUserOptions } from "../../../user/expertUsersSlice";
import { SCHEDULING_PERIOD_TYPES } from "../../../settings/helpers/utils";
import { EezyCompany } from "../../../invoices/invoiceAPI";
import { selectAllCustomerContracts } from "../../customerContractsSlice";
import { CustomerContract } from "../../customerAPI";

export const CreateCustomerContractForm = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["customers", "common"]);

  const customerContracts: CustomerContract[] = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  );
  const salesPersonOptions = useAppSelector((state) =>
    selectActiveExpertUserOptions(state),
  );
  const { schedulingClass } = useAppSelector((state) => state.localized);
  const schedulingClassOptions = useMemo(
    () =>
      SCHEDULING_PERIOD_TYPES.map((type) => ({
        id: type.id,
        name: schedulingClass[type.id] || "",
      })).sort((a, b) => a.name.localeCompare(b.name)),
    [schedulingClass],
  );

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [eezyCompanyOptions, setEezyCompanyOptions] = useState<EezyCompany[]>(
    [],
  );

  useEffect(() => {
    dispatch(fetchEezyCompanies({})).then((action) => {
      if (isFulfilled(action)) {
        const companiesWithoutContract = action.payload.filter(
          (company) =>
            customerContracts
              .map((contract) => contract.eezyCompany.id)
              .includes(company.eezyCompanyId) === false,
        );
        setEezyCompanyOptions(companiesWithoutContract);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <Grid container spacing={2} sx={{ margin: "8px" }}>
      <FormGridRow
        label={t("common:customer")}
        labelSize={5}
        fieldSize={7}
        GridLabelProps={{ sx: { alignItems: "top", marginTop: "8px" } }}
      >
        <Controller
          control={control}
          name="customerCompany"
          render={({ field }) => (
            <Typography sx={{ marginTop: "8px" }}>
              {field.value?.name}
            </Typography>
          )}
        />
      </FormGridRow>
      <FormGridRow
        label={t("customers:contract:eezyCompany")}
        labelSize={5}
        fieldSize={7}
      >
        <Controller
          control={control}
          name="eezyCompany"
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={eezyCompanyOptions}
              getOptionLabel={(option) => option.name || ""}
              renderInput={(params) => (
                <CommonTextField {...params} error={!!errors.name} fullWidth />
              )}
              onChange={(_, value) => {
                field.onChange(value);
              }}
              isOptionEqualToValue={(option, value) =>
                option.eezyCompanyId === value.eezyCompanyId
              }
              fullWidth
            />
          )}
        />
      </FormGridRow>
      <FormGridRow
        label={t("customers:contract:eezySalesPerson")}
        labelSize={5}
        fieldSize={7}
      >
        <Controller
          control={control}
          name="salesPerson"
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={salesPersonOptions}
              getOptionLabel={(option) => option.name || ""}
              renderInput={(params) => (
                <CommonTextField
                  {...params}
                  error={!!errors.salesPerson}
                  fullWidth
                />
              )}
              onChange={(_, value) => {
                field.onChange(value);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ maxWidth: "372px" }}
              fullWidth
            />
          )}
        />
      </FormGridRow>
      <FormGridRow
        label={t("hourlySchedulingClass")}
        labelSize={5}
        fieldSize={7}
      >
        <Controller
          control={control}
          name="schedulingClass"
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={schedulingClassOptions}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <CommonTextField
                  {...params}
                  error={!!errors.schedulingClass}
                  fullWidth
                />
              )}
              onChange={(_, value) => {
                field.onChange(value);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ maxWidth: "372px" }}
              fullWidth
            />
          )}
        />
      </FormGridRow>
      <FormGridRow
        label={t("monthlySchedulingClass")}
        labelSize={5}
        fieldSize={7}
      >
        <Controller
          control={control}
          name="monthlyWorkSchedulingClass"
          rules={{ required: true }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              options={schedulingClassOptions}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <CommonTextField
                  {...params}
                  error={!!errors.monthlyWorkSchedulingClass}
                  fullWidth
                />
              )}
              onChange={(_, value) => {
                field.onChange(value);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ maxWidth: "372px" }}
              fullWidth
            />
          )}
        />
      </FormGridRow>
    </Grid>
  );
};
